export type ImageCropProfile = {
  title: string;
  description: string;
  width: number;
  height: number;
  round?: boolean;
  previewScale?: number;
};

const imageCropProfiles: Record<string, ImageCropProfile> = {
  'course-cover': {
    title: '裁剪课程封面',
    description:
      '首页、课程列表、详情页及手机端的课程封面统一使用 16:9。裁剪后输出 1200×675，请保留标题和画面主体，无需为不同页面重复裁剪。',
    width: 1200,
    height: 675,
  },
  'material-cover': {
    title: '裁剪资料封面',
    description:
      '按桌面资料封面的 16:9 比例裁剪，其他屏幕尺寸自动适配，无需另行裁剪。请保留标题和画面主体。',
    width: 1200,
    height: 675,
  },
  share: {
    title: '裁剪分享封面',
    description: '分享封面采用独立的 1200:630 分享规格，不使用网页封面比例。请保留标题和主体。',
    width: 1200,
    height: 630,
  },
};

export function getTeacherCropSize(viewportWidth: number) {
  const pageWidth = Math.min(viewportWidth, 1400);
  const padding = viewportWidth <= 860 ? 18 : Math.max(18, Math.min(viewportWidth * 0.04, 44));
  const railWidth = pageWidth - padding * 2 - 4;
  const columns = viewportWidth <= 860 ? 2 : viewportWidth <= 1080 ? 3 : 4;
  const photoWidth =
    (viewportWidth <= 520 ? railWidth * 0.86 : (railWidth - (columns - 1) * 14) / columns) - 2;
  const photoHeight = Math.max(200, Math.min(viewportWidth * 0.17, 258)) - 1;
  return { width: Math.round((photoWidth / photoHeight) * 900), height: 900 };
}

export function getImageCropProfile(
  kind?: string,
  { lead = false }: { lead?: boolean } = {},
): ImageCropProfile | undefined {
  if (kind === 'teacher') {
    if (lead)
      return {
        title: '裁剪领衔老师头像',
        description: '领衔老师为圆形头像，输出 900×900；预览同步官网现有的 1.42 倍放大效果。',
        width: 900,
        height: 900,
        round: true,
        previewScale: 1.42,
      };
    return {
      title: '裁剪普通老师照片',
      description:
        '可选裁剪：按桌面端（1440px）的矩形比例输出，官网完整显示裁剪结果。请保留人物和肩部；直接上传可保留完整原图。',
      ...getTeacherCropSize(1440),
    };
  }
  return kind ? imageCropProfiles[kind] : undefined;
}

export function getCropGeometry(
  imageWidth: number,
  imageHeight: number,
  ratio: number,
  zoom: number,
) {
  const baseWidth = Math.min(imageWidth, imageHeight * ratio);
  const cropWidth = baseWidth / zoom;
  const cropHeight = cropWidth / ratio;
  return {
    cropWidth,
    cropHeight,
    maxX: imageWidth - cropWidth,
    maxY: imageHeight - cropHeight,
    widthPercent: (imageWidth / cropWidth) * 100,
    heightPercent: (imageHeight / cropHeight) * 100,
  };
}

export function getCropPreviewStyle(
  geometry: ReturnType<typeof getCropGeometry>,
  positionX: number,
  positionY: number,
  scale = 1,
) {
  return {
    width: `${geometry.widthPercent * scale}%`,
    height: `${geometry.heightPercent * scale}%`,
    left: `${-((positionX + 100) / 200) * (geometry.widthPercent - 100) * scale - (scale - 1) * 50}%`,
    top: `${-((positionY + 100) / 200) * (geometry.heightPercent - 100) * scale - (scale - 1) * 30}%`,
  };
}

export function moveCropPosition(position: number, delta: number, overflow: number) {
  if (overflow <= 0) return position;
  return Math.max(-100, Math.min(100, position - (delta / overflow) * 200));
}
